import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { Breadcrumbs, LoadingOverlay } from '../../../../';
import { getCookie, getCountryFromPostal, getLocalStorage, getSessionStorage, htmlDecode, initDataLayer, monetateDataLayer, setCookie } from '../../../..//utilities/functions';
import PromoBanner from '../../../..//common/promo-banner';
import { Alert, Button, Checkbox, Column, Container, Icon, List, Modal, Row, Select, TextArea } from '@pella/sash-framework';
import { isValidPostal } from '../../../..//utilities/validations';
import { useDispatch, useSelector } from 'react-redux';
import { useBranchContext } from '../../../../../context/BranchContext';
import AddressValidation from '../../../..//common/address-validation/v3';
import { RTA_COMMON, RTA_ERRORS, phoneReplace, emailRegex, poBoxRegex } from '../../../..//utilities/constants';
import { getCalendar, nextId, resetRTACalls, timeblock, appointment, storeAppointment, randomId } from '../../../../../actions/rta-actions';
import smoothScroll from '../../../..//common/smooth-scroll';
import { initialCalendar } from '../../../..//page/rta/v2/calendar/initial-calendar';
import queryString from 'query-string';
import APPTCalendar from '../../../..//common/appointments/calendar';
import APPTTimeSlots from '../../../..//common/appointments/time-slots';
import { captureMessage, setContext, captureException, getCurrentScope } from '@sentry/gatsby';
import { useLocation as useRouterLocation } from '@reach/router';
import { useBranchId } from '../../../../../hooks/use-branch-id';
import { useGlobalConfig } from '../../../../../hooks/use-global-config';
import { useBranchData } from '../../../../../hooks/use-branch-data';
import { MY_APPOINTMENTS, SUBMIT_PRE_RTA_FORM } from '../../../../../enums/graphql';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useOktaAuth } from '../../../../../hooks/use-okta-auth';
import { useMediaQuery } from 'react-responsive';
import { useExitIntent } from '../../../../../hooks/use-exit-intent';
import { useLocation } from '../../../../../context/LocationContext';
import FifteenMinuteApptExitIntent from '../../../../common/fifteen-minute-appointment/exit-intent';
import ContactInfoFields from '../../contact-info-fields';
import Recaptcha from '../../../../common/forms/recaptcha';
import HoverBannerRTA from '../../../../third-party/hover/rta-banner';
import ContactCard from '../../../../common/fifteen-minute-appointment/contact-card';

const ScheduleConsultation = ({ content, renderAs = 'page', src = 'rta' }) => {
	const { contentstackStandardRta, contentstackRtaForm, contentstackConversionConfig } = useStaticQuery(query);
	const { breadcrumbs, body_copy, address_validation, preference_appointment, figured_out, go_back, calendar } = content || contentstackStandardRta;
	const { checkboxes, form_labels, form_heading, captcha: captchaBranches } = contentstackRtaForm;
	const { professional } = contentstackConversionConfig;
	const { postal_code_change, session_timeout } = useGlobalConfig();
	const branches = useBranchData();
	const rtaData = useSelector((state) => state.rtaData);
	const dispatch = useDispatch();
	const timeoutWarning = useRef(null);
	const { updateCurrentBranch } = useBranchContext();
	const location = useRouterLocation();
	const currentUrl = location?.href;
	const referrer = location?.state?.referrer;
	const sem_referral = getSessionStorage('sem_referral');
	const branchId = useBranchId();
	const isDesktop = useMediaQuery({ minWidth: 1280 });
	const { profile, oktaInitialized } = useOktaAuth();
	const { isPro } = profile;
	const { setLocation } = useLocation();

	const formBaseState = {
		postal: '',
		firstname: '',
		lastname: '',
		email: '',
		phone: '',
		windows: '',
		doors: '',
		// Hidden field to help scan for bots
		householdPets: '',
		previousAddress: {
			line_1: '',
			line_2: '',
			city: '',
			state: '',
			postcode: '',
		},
	};

	const [timeLeft, setTimeLeft] = useState(0);
	const clockRef = useRef(timeLeft);
	clockRef.current = timeLeft;

	const [mounted, setMounted] = useState(false);
	const [clock, setClock] = useState(false);
	const [complete, setComplete] = useState(false);
	const [errors, setErrors] = useState({});
	const [modalOpen, setModalOpen] = useState(false);
	const [sessionTimerModal, setSessionTimerModal] = useState(false);
	const [isDesignworksLead, setIsDesignworksLead] = useState(false);
	const [addressErrors, setAddressErrors] = useState({});
	const [addressComplete, setAddressComplete] = useState(false);
	const [addressModified, setAddressModified] = useState(false);
	const [forceAddressValidation, setForceAddressValidation] = useState(false);
	const [usePreviousAddress, setUsePreviousAddress] = useState(false);
	const [shouldRenderForm, setShouldRenderForm] = useState(false);
	const [branchData, setBranchData] = useState({});
	const [speedcurveCalendarTimingStart, setSpeedcurveCalendarTimingStart] = useState(null);
	const [loading, setLoading] = useState(false);

	const [formData, setFormData] = useState({
		...formBaseState,
		previousZip: '',
		comments: '',
		privacyCheck: false,
		contactCheck: true,
	});

	const [address, setAddress] = useState({
		addressValidated: false,
		userValidated: false,
		userEntered: false,
	});

	const [apiData, setApiData] = useState({
		initialCalendar: initialCalendar(),
		timeslots: [],
		isInitial: true,
		territoryGroup: '',
		timezoneCode: '',
		hasShowroom: false,
		waitingOnShowrooms: true,
		branchID: '',
		recID: '',
		appointmentStatus: '',
	});

	const [apptInfo, setApptInfo] = useState({
		appointmentType: '', // ['home', 'showroom', 'preference', 'nrt', 'virtual']
		startDate: '',
		endDate: '',
		preferenceDay: '',
		preferenceTime: '',
		arrayStartPosition: null,
		timeslotLength: null,
		selectedDay: 0,
		selectedTimeslot: -1,
		firstCalendarLoad: true,
	});

	const [showroomInfo, setShowroomInfo] = useState({
		showroomName: '',
		showroomAddress: '',
		showroomCity: '',
		showroomState: '',
		showroomCountry: '',
		showroomZip: '',
		showroomEmail: '',
		showroomPhone: '',
		showroomWeekHours: '',
		showroomSatHours: '',
		showroomSunHours: '',
	});

	const [formStatus, setFormStatus] = useState({
		loading: false,
		submitDisabled: true,
		showroomAlert: false,
		hideShowrooms: true,
		loadDisabled: true,
		tempStore: false,
		tempObject: {},
	});

	useEffect(() => {
		dispatch(resetRTACalls());
		let postalParam = '';
		let appointmentType = RTA_COMMON.TYPE.HOME;
		if (window !== undefined) {
			if (!apiData.recID) {
				dispatch(nextId());
			}
			const queryParams = queryString.parse(window.location.search);
			if (queryParams.postalCode) {
				postalParam = queryParams.postalCode;
			}
			if (queryParams.appointmentType && queryParams.appointmentType === RTA_COMMON.TYPE.VIRTUAL) {
				appointmentType = RTA_COMMON.TYPE.VIRTUAL;
			}
		}
		if (postalParam) {
			if (isValidPostal(postalParam)) {
				setMounted(true);
				setFormStatus({
					...formStatus,
					loading: true,
					loadDisabled: true,
				});
				setApiData({ ...apiData, isInitial: true });
				setApptInfo({
					...apptInfo,
					appointmentType: appointmentType,
					waitingOnShowrooms: true,
					aptSelected: appointmentType,
				});
			} else {
				setMounted(true);
				setFormStatus({ ...formStatus, loadDisabled: false });
				setApptInfo({
					...apptInfo,
					appointmentType: appointmentType,
					aptSelected: appointmentType,
				});
			}
		} else {
			setMounted(true);
			setApptInfo({
				...apptInfo,
				appointmentType: appointmentType,
				aptSelected: appointmentType,
			}); // PW-5453 - Added virtual
		}

		monetateDataLayer('setPageType', 'RTA');
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, []);

	useEffect(() => {
		let params = new URLSearchParams(location.search);
		let designworks = params.get('dw');

		if (designworks) setIsDesignworksLead(true);

		if (location.state?.designworks_lead) {
			setIsDesignworksLead(true);
		}

		if (location.state?.userAddress) {
			setAddress(location.state.userAddress);
			setFormData((curr) => ({
				...curr,
				referrer: location.state.referrer,
				previousAddress: {
					line_1: location.state.userAddress.address1,
					line_2: '',
					city: location.state.userAddress.city,
					state: location.state.userAddress.state,
					postcode: location.state.userAddress.postalCode,
				},
			}));

			setLoading(true);
			setTimeout(() => {
				// give time for the address validation component to catch up
				document.getElementById('js-addressSubmit')?.click();
				setLoading(false);
			}, 1000);
		}
	}, [location.state, location.search]);

	useEffect(() => {
		if (renderAs !== 'page') return;

		let params = new URLSearchParams(window.location.search);
		let state = params.get('state');

		if (branchId) {
			const branchData = branches?.find((branchData) => branchData.branch_retail?.includes(branchId) && (state ? branchData.state?.abbreviation?.toUpperCase() === state?.toUpperCase() : true));

			if (branchData) {
				setBranchData(branchData);
				updateCurrentBranch(branchData);
			}
		}
	}, [branchId, branches, updateCurrentBranch, renderAs]);

	// START: Might need to add checks inside a "mounted" if check
	useEffect(() => {
		// Checking for opp number API response
		if (!apiData.recID && rtaData.OppNo && apiData.recID !== rtaData.OppNo) {
			setApiData({ ...apiData, recID: rtaData.OppNo });
			dispatch(resetRTACalls()); // Need to remove this oppNumber from redux state, or sometimes oppNumber doesn't always change on refresh
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [rtaData.OppNo]);

	useEffect(() => {
		if (apiData?.branchID) {
			initDataLayer('getBranchID', { branchID: apiData.branchID });
		}
	}, [apiData?.branchID]);

	useEffect(() => {
		// Checking for resource not available error message
		if (rtaData.errorMessage === RTA_ERRORS.RESOURCE_UNAVAILABLE) {
			refreshCalendar();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [rtaData.errorMessage]);

	// Calendar Timings & Errors
	useEffect(() => {
		if (speedcurveCalendarTimingStart !== null && (rtaData.status === 'success' || rtaData.status === 'failed')) {
			let LUX = window.LUX || {};
			let timing = (performance.now() - speedcurveCalendarTimingStart) / 1000;

			if (rtaData?.status === 'success' && typeof LUX.addData === 'function') {
				LUX.addData('rtaCalenderLoadTime', timing);
				LUX.addData('rtaCalendarLoadFailed', false);
			} else if (rtaData?.status === 'failed' && typeof LUX.addData === 'function') {
				LUX.addData('rtaCalenderLoadTime', timing);
				LUX.addData('rtaCalendarLoadFailed', true);
				setContext('Error Data', {
					httpStatusCode: rtaData.httpStatusCode,
					appointmentType: rtaData.AppointmentType,
					branchID: rtaData.BranchID,
					error: rtaData.errorMessage,
				}),
					captureMessage('RTA Alerts - Get Calendar Error FE');
			}
			setSpeedcurveCalendarTimingStart(null);
		}
	}, [rtaData, speedcurveCalendarTimingStart]);

	useEffect(() => {
		// Checking for calendar response
		let newApiData = {};
		let newApptInfo = {};
		if (apiData.recID) {
			if (rtaData.TerritoryZone && rtaData.TimezoneInfo && rtaData.BranchID) {
				if (rtaData.TerritoryZone === RTA_COMMON.ZONE.RT_ZONE) {
					if (rtaData.Timeslots && rtaData.Timeslots.length !== 0) {
						let day = parseInt(rtaData?.Timeslots[0]?.START_DATE?.split('T')[0]?.split('-')[2]);
						let length = 0;
						for (let i = 0; i < rtaData.Timeslots.length; i++) {
							if (parseInt(rtaData?.Timeslots[i]?.START_DATE?.split('T')[0]?.split('-')[2]) !== day) {
								break;
							}
							length++;
						}
						newApiData = {
							timeslots: rtaData.Timeslots,
							territoryGroup: rtaData.TerritoryZone,
							timezoneCode: rtaData.TimezoneInfo?.STANDARD_TIME_SHORT_CODE || '',
							branchID: rtaData.BranchID,
							isInitial: false,
						};
						newApptInfo = {
							selectedDay: parseInt(day),
							arrayStartPosition: 0,
							timeslotLength: length,
							selectedTimeslot: 0,
							appointmentType: [RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.VIRTUAL].includes(apptInfo.appointmentType) ? apptInfo.appointmentType : RTA_COMMON.TYPE.HOME,
							startDate: rtaData.Timeslots[0].START_DATE,
							endDate: rtaData.Timeslots[0].END_DATE,
						};
						setTimer(); // Set/Reset timer
						setApiData({
							...apiData,
							...newApiData,
						});
						setApptInfo({
							...apptInfo,
							...newApptInfo,
						});
					} else if (rtaData.Timeslots && rtaData.Timeslots.length === 0) {
						newApiData = {
							timeslots: rtaData.Timeslots,
							territoryGroup: rtaData.TerritoryZone,
							timezoneCode: rtaData.TimezoneInfo?.STANDARD_TIME_SHORT_CODE || '',
							branchID: rtaData.BranchID,
							isInitial: false,
						};
						newApptInfo = {
							selectedDay: 0,
							arrayStartPosition: null,
							timeslotLength: null,
							selectedTimeslot: -1,
							appointmentType: RTA_COMMON.TYPE.PREFERENCE,
							startDate: '',
							endDate: '',
						};
						clearTimer();
						setApiData({
							...apiData,
							...newApiData,
						});
						setApptInfo({
							...apptInfo,
							...newApptInfo,
						});
					}
				} else {
					newApiData = {
						timeslots: rtaData.Timeslots,
						territoryGroup: rtaData.TerritoryZone,
						timezoneCode: rtaData.TimezoneInfo?.STANDARD_TIME_SHORT_CODE || '',
						branchID: rtaData.BranchID,
						isInitial: false,
					};
					newApptInfo = {
						selectedDay: 0,
						arrayStartPosition: null,
						timeslotLength: null,
						selectedTimeslot: -1,
						appointmentType: RTA_COMMON.TYPE.NRT,
						startDate: '',
						endDate: '',
					};
					clearTimer();
					setApiData({
						...apiData,
						...newApiData,
					});
					setApptInfo({
						...apptInfo,
						...newApptInfo,
					});
				}
			} else if (!rtaData.TerritoryZone && apiData.territoryGroup !== rtaData.TerritoryZone && rtaData.status === 'failed') {
				newApiData = {
					timeslots: rtaData.Timeslots || [],
					territoryGroup: null,
					timezoneCode: rtaData?.TimezoneInfo?.STANDARD_TIME_SHORT_CODE || '',
					branchID: rtaData.BranchID,
					isInitial: false,
				};
				newApptInfo = {
					selectedDay: 0,
					arrayStartPosition: null,
					timeslotLength: null,
					selectedTimeslot: -1,
					appointmentType: RTA_COMMON.TYPE.NRT,
					startDate: '',
					endDate: '',
				};
				clearTimer();
				setApiData({
					...apiData,
					...newApiData,
				});
				setApptInfo({
					...apptInfo,
					...newApptInfo,
				});
				setErrors({
					...errors,
					appointment: calendar.loading_error,
				});
			}

			if (!apiData.appointmentStatus) {
				isFormComplete(
					{
						...formData,
						...apptInfo,
						...apiData,
						...address,
						...showroomInfo,
						...newApiData,
						...newApptInfo,
					},
					{ loading: false }
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [rtaData.TerritoryZone, JSON.stringify(rtaData.TimezoneInfo), rtaData.BranchID, JSON.stringify(rtaData.Timeslots), rtaData.AppointmentType, rtaData.status]);

	useEffect(() => {
		// Checking appointment status
		if (rtaData.AppointmentStatus?.status) {
			if (rtaData.AppointmentStatus.status.toLowerCase() !== 'success') {
				dispatch(storeAppointment(formStatus.tempObject));
				setApiData({
					...apiData,
					appointmentStatus: rtaData.AppointmentStatus.status,
				});
				setLoading(false);
				setFormStatus({ ...formStatus, tempStore: true });
				setContext('Error Data', {
					errorMessage: rtaData.AppointmentStatus?.errorMessage,
					httpStatusCode: rtaData.AppointmentStatus?.httpStatusCode,
				}),
					captureMessage('RTA Alerts - Appointment Error');
			} else {
				setApiData({
					...apiData,
					appointmentStatus: rtaData.AppointmentStatus.status,
				});
			}
		} else if (rtaData.submitError !== undefined && formStatus.tempStore !== rtaData.submitError) {
			setFormStatus({ ...formStatus, tempStore: true });
		} else if (rtaData.TempStatus !== undefined && apiData.appointmentStatus !== rtaData.TempStatus) {
			setApiData({
				...apiData,
				appointmentStatus: rtaData.TempStatus === true ? 'success' : 'failed',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [JSON.stringify(rtaData.AppointmentStatus), rtaData.submitError, rtaData.TempStatus]);
	// END: Might need to add checks inside a "mounted" if check

	useEffect(() => {
		let params = new URLSearchParams(location.search);
		let fromHover = false;

		if (params.get('utm_source') === 'hover') {
			fromHover = true;
		}

		if (apiData.appointmentStatus && apiData.appointmentStatus.toLowerCase() === 'success') {
			const state = {
				...formData,
				...address,
				...apiData,
				...apptInfo,
				...showroomInfo,
				...formStatus,
				branchData: branchData,
				referredByHover: fromHover,
			};

			const combinedDataLayer = {
				events: [
					{
						event: 'rta-form-submit',
						Opportunity_No__c: apiData.recID,
						email_address: formData.email,
						phone_number: formData.phone,
						first_name: formData.firstname,
						last_name: formData.lastname,
						country: address.country,
						postal_code: formData.postal,
					},
					{
						event: 'getBranchID',
						branchID: apiData.branchID,
					},
				],
			};

			combinedDataLayer.events.forEach((event) => {
				initDataLayer(event.event, event);
			});

			if (apptInfo?.startDate) {
				setCookie('mon_appt_info', apptInfo.startDate, new Date(apptInfo.startDate), true);
			}
			dispatch(resetRTACalls());
			navigate('/where-to-buy/schedule-consultation/confirmation/', {
				state: state,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [apiData.appointmentStatus]);

	useEffect(() => {
		// Watch for postal and postalCode mismatching
		if (shouldRenderForm && !modalOpen && address.postalCode !== formData.postal && isValidPostal(address.postalCode)) {
			let open = false;
			if (getCountryFromPostal(formData.postal) === 'CAN') {
				if (formData.postal.replace(/\-| /gi, '') !== address.postalCode.replace(/\-| /gi, '')) {
					open = true;
				}
			} else if (formData.postal.substring(0, 5) !== address.postalCode.substring(0, 5)) {
				open = true;
			}
			if (open && !modalOpen) {
				setModalOpen(open);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [formData.postal, address.postalCode]);

	useEffect(() => {
		// watch when startDate has been set/changed
		if (apptInfo.startDate) {
			const customerData = {
				oppNo: apiData.recID,
				start: apptInfo.startDate,
				end: apptInfo.endDate,
				type: apptInfo.firstCalendarLoad ? RTA_COMMON.BLOCK_TYPE.BLOCK : RTA_COMMON.BLOCK_TYPE.UNBLOCK_BLOCK,
				zipcode: formData.postal,
				appType: apptInfo.appointmentType,
			};
			if (apptInfo.firstCalendarLoad) {
				setApptInfo({
					...apptInfo,
					firstCalendarLoad: false,
				});
			}
			dispatch(timeblock(customerData));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [apptInfo.startDate]);

	useEffect(() => {
		let interval;
		if (clock) {
			interval = setInterval(() => {
				let currCount = clockRef.current;
				if (currCount > 0) {
					setTimeLeft((currCount) => currCount - 1);
				} else {
					clearInterval(interval);
					refreshCalendar();
				}
			}, 1000);
		} else {
			clearInterval(interval);
		}

		return () => {
			clearInterval(interval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [clock]);

	const clearTimer = (stopClock = true) => {
		if (typeof timeoutWarning.current === 'number') {
			clearTimeout(timeoutWarning.current);
			timeoutWarning.current = null;
		}
		if (stopClock) {
			setClock(false);
		}
	};

	const setTimer = () => {
		if (showing15MinApptCalendar || sessionTimerModal) {
			setSessionTimerModal(false);
		}
		clearTimer(false);
		timeoutWarning.current = setTimeout(
			() => {
				setSessionTimerModal(true);
				setClock(true);
				setTimeLeft(300);
			},
			1000 * 60 * 25
		);
	};

	const handleSessionModalClose = () => {
		setSessionTimerModal(false);
		setClock(false);
		setTimer();
		if (apptInfo.startDate) {
			const customerData = {
				oppNo: apiData.recID,
				start: apptInfo.startDate,
				end: apptInfo.endDate,
				type: RTA_COMMON.BLOCK_TYPE.UNBLOCK_BLOCK,
				zipcode: formData.postal,
				appType: apptInfo.appointmentType,
			};
			dispatch(timeblock(customerData));
		}
	};

	const handleChange = (e) => {
		const field = e.target.name;
		const value = e.target.value;
		setFormData({ ...formData, [field]: value });
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
		});
	};

	const handleBlur = (e) => {
		if (errors[e.target.name] && e.target.value) {
			setErrors({ ...errors, [e.target.name]: '' });
		}
	};

	const handlePreferenceChange = (e) => {
		const field = e.target.name;
		const value = e.target.value;
		const newApptInfo = {
			[field]: value,
		};
		setApptInfo({
			...apptInfo,
			...newApptInfo,
		});
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
			...newApptInfo,
		}); // current state followed by any changes
	};

	const handleNumberChange = (name, value) => {
		let newFormStatus = {};
		let newAddress = {};
		let newApiData = {};
		let newApptInfo = {};

		const newFormData = {
			[name]: value,
		};
		setFormData({ ...formData, ...newFormData });
		isFormComplete(
			{
				...formData,
				...apptInfo,
				...apiData,
				...address,
				...showroomInfo,
				...newFormData,
				...newAddress,
				...newApiData,
				...newApptInfo,
			},
			newFormStatus
		);
	};

	const handleCheck = (e) => {
		const field = e.target.id;
		const value = e.target.checked;
		const newFormData = {
			[field]: value,
		};
		if (errors[field] && value) {
			setErrors({ ...errors, [field]: null });
		}
		setFormData({ ...formData, ...newFormData });
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
			...newFormData,
		});
	};

	const handleDaySelect = (start, length, id) => {
		const newApptInfo = {
			arrayStartPosition: start,
			timeslotLength: length,
			selectedDay: id,
			selectedTimeslot: -1,
			startDate: '',
			endDate: '',
		};
		setApptInfo({
			...apptInfo,
			...newApptInfo,
		});
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
			...newApptInfo,
		});
	};

	const handleTimeslotSelect = (timeslot, index) => {
		const newApptInfo = {
			startDate: timeslot.START_DATE,
			endDate: timeslot.END_DATE,
			selectedTimeslot: index,
		};
		setApptInfo({
			...apptInfo,
			...newApptInfo,
		});
		isFormComplete({
			...formData,
			...apptInfo,
			...apiData,
			...address,
			...showroomInfo,
			...newApptInfo,
		});
	};

	const validateEmail = (value) => {
		if (value.match(emailRegex) === null) {
			return false;
		}
		return true;
	};

	const validatePhone = (value) => {
		if (value.replace(phoneReplace, '').length !== 10) {
			return false;
		}
		return true;
	};

	const validateNumberField = (name, value) => {
		if (!value || value == '0') {
			return '';
		}
		if (Number.isNaN(value)) {
			return `${name.charAt(0).toUpperCase() + name.substring(1)} must be a number`;
		}
		return '';
	};

	const loadCalendar = (e) => {
		setSpeedcurveCalendarTimingStart(performance.now());
		e.preventDefault();
		dispatch(resetRTACalls());
		dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, address.postalCode, apptInfo.appointmentType));

		const newApiData = {
			waitingOnShowrooms: true,
			timeslots: [],
			territoryGroup: '',
			timezoneCode: '',
			branchID: '',
			isInitial: true,
		};
		setApiData({ ...apiData, ...newApiData });
		setFormStatus({
			...formStatus,
			loading: true,
			loadDisabled: true,
			submitDisabled: true,
		});
		setComplete(false);

		setTimeout(() => {
			let path = '';
			if (apptInfo.appointmentType === RTA_COMMON.TYPE.HOME || apptInfo.appointmentType === RTA_COMMON.TYPE.VIRTUAL) {
				if (apiData.hasShowroom) {
					path = 'js-ShowroomBanner';
				} else {
					path = 'js-Calendar';
				}
			} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				path = 'js-Preference';
			} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.NRT) {
				path = 'js-NRT';
			}
			if (path !== '') {
				smoothScroll.scrollTo(path, -45);
			}
		}, 500);

		initDataLayer('load-calendar', {
			calendarZip: formData.postal,
			calendarAppType: apptInfo.appointmentType,
		});

		monetateDataLayer('setPageType', 'RTA_Calendar');
	};

	const refreshCalendar = () => {
		let path = '';
		if (apptInfo.appointmentType === RTA_COMMON.TYPE.HOME || apptInfo.appointmentType === RTA_COMMON.TYPE.VIRTUAL) {
			if (apiData.hasShowroom) {
				path = 'js-ShowroomBanner';
			} else {
				path = 'js-Calendar';
			}
		} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
			path = 'js-Preference';
		} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.NRT) {
			path = 'js-NRT';
		}
		if (path !== '') {
			smoothScroll.scrollTo(path, -45);
		}
		if (apptInfo.startDate && !apiData.isInitial && !formStatus.loading) {
			const customerData = {
				oppNo: apiData.recID,
				start: apptInfo.startDate,
				end: apptInfo.endDate,
				type: RTA_COMMON.BLOCK_TYPE.UNBLOCK,
				zipcode: formData.postal,
				appType: apptInfo.appointmentType,
			};
			dispatch(timeblock(customerData));
		}
		setApptInfo({
			...apptInfo,
			startDate: '',
			endDate: '',
			selectedDay: 0,
			selectedTimeslot: -1,
			arrayStartPosition: null,
			timeslotLength: null,
			firstCalendarLoad: true,
		});
		setFormStatus({ ...formStatus, loading: true, submitDisabled: true });
		setApiData({
			...apiData,
			isInitial: true,
			timeslots: [],
			territoryGroup: '',
			timezoneCode: '',
			branchID: '',
		});
		setComplete(false);
		dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, formData.postal, apptInfo.appointmentType));
	};

	const validateContactInfo = () => {
		let updatedErrors = {};

		if (!formData.firstname) {
			updatedErrors.firstname = RTA_ERRORS.FIRSTNAME_ERROR_HANDLER;
		}
		if (!formData.lastname) {
			updatedErrors.lastname = RTA_ERRORS.LASTNAME_ERROR_HANDLER;
		}
		if (!formData.email || (formData.email && !validateEmail(formData.email))) {
			updatedErrors.email = RTA_ERRORS.EMAIL_ERROR_HANDLER;
		}
		if (!formData.phone || (formData.phone && !validatePhone(formData.phone))) {
			updatedErrors.phone = RTA_ERRORS.PHONE_ERROR_HANDLER;
		}

		return updatedErrors;
	};

	const validateForm = () => {
		let updatedErrors = validateContactInfo();

		if (!apptInfo.appointmentType) {
			updatedErrors.appointmentType = RTA_ERRORS.APPOINTMENT_TYPE_ERROR_HANDLER;
		}
		if ([RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.SHOWROOM, RTA_COMMON.TYPE.VIRTUAL].includes(apptInfo.appointmentType) && !apptInfo.startDate) {
			updatedErrors.appointment = RTA_ERRORS.DATE_TIME_ERROR_HANDLER;
		} else if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
			if (!apptInfo.preferenceDay) {
				updatedErrors.preferenceDay = RTA_ERRORS.PREFERRED_DAY_ERROR_HANDLER;
			}
			if (!apptInfo.preferenceTime) {
				updatedErrors.preferenceTime = RTA_ERRORS.PREFERRED_TIME_ERROR_HANDLER;
			}
		}
		if (!formData.privacyCheck) {
			updatedErrors.privacyCheck = RTA_ERRORS.PRIVACY_ERROR_HANDLER;
		}

		if (captchaBranches.includes(branchId) && !captchaToken) {
			updatedErrors.captcha = RTA_ERRORS.CAPTCHA_ERROR_HANDLER;
		}

		let formValid = true;
		if (Object.keys(updatedErrors).length > 0) {
			formValid = false;
			setErrors(updatedErrors);
		}
		return formValid;
	};

	const validateAddress = () => {
		let errors = {};
		if (!address.address1 || address.address1 === '' || address.address1.match(poBoxRegex)) {
			errors.address1 = RTA_ERRORS.ADDRESS_LINE_1_ERROR_HANDLER;
		}
		if (!address.city || address.city === '') {
			errors.city = RTA_ERRORS.CITY_ERROR_HANDLER;
		}
		if (!address.state || address.state === '') {
			errors.state = RTA_ERRORS.STATE_ERROR_HANDLER;
		}
		if (!isValidPostal(address.postalCode)) {
			errors.postalCode = RTA_ERRORS.POSTAL_ERROR_HANDLER;
		}

		setAddressErrors(errors);

		if (Object.keys(errors).length > 0) {
			window.scroll({ top: 0, behavior: 'smooth' });
			setForceAddressValidation(true);
			return false;
		} else {
			return true;
		}
	};

	const addressCheck = (object) => {
		if (!object.address1 || !object.city || !object.state || !isValidPostal(object.postalCode) || !object.country) {
			return true;
		}
		return false;
	};

	const isFormComplete = (object, statusChange = {}) => {
		let isComplete = true;
		if (!isValidPostal(object.postal) || !object.firstname || !object.lastname || !object.email || !object.phone) {
			isComplete = false;
		} else if (validateEmail(object.email) || validatePhone(object.phone)) {
			isComplete = false;
		} else if (validateNumberField(object.windows) || validateNumberField(object.doors)) {
			isComplete = false;
		} else if (!object.userEntered && (!object.addressValidated || addressCheck(object))) {
			isComplete = false;
		} else if (object.userEntered && (!object.userValidated || addressCheck(object))) {
			isComplete = false;
		} else if (!object.privacyCheck) {
			isComplete = false;
		} else if (!object.branchID) {
			isComplete = false;
		} else {
			if ([RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.VIRTUAL].includes(object.appointmentType)) {
				if (!object.startDate || !object.endDate) {
					isComplete = false;
				}
			} else if (object.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				if (!object.preferenceDay || !object.preferenceTime) {
					isComplete = false;
				}
			} else if (object.appointmentType !== RTA_COMMON.TYPE.NRT) {
				isComplete = false;
			}
		}
		if (isComplete !== complete) {
			if (isComplete) {
				setErrors({});
			}
		}
		setComplete(isComplete);
		setFormStatus({
			...formStatus,
			...statusChange,
			submitDisabled: !isComplete,
		});
	};

	const autoCapitalize = (string) => string?.[0]?.toUpperCase() + string?.slice(1);

	const handleSubmit = (e) => {
		e.preventDefault();

		setLoading(true);

		if (!validateForm()) {
			const scrollPoint = document.getElementById('js-Calendar')?.offsetTop - 150;
			window.scroll({ top: scrollPoint || 0, behavior: 'smooth' });
			setLoading(false);
			return;
		}

		if (!validateAddress()) {
			const scrollPoint = document.getElementById('js-Calendar')?.offsetTop - 150;
			window.scroll({ top: scrollPoint || 0, behavior: 'smooth' });
			setLoading(false);
			return;
		}

		let comments = formData.comments;

		if (isDesignworksLead) {
			comments += ' - design works lead';
		}

		// Submit form
		let urlstring = getSessionStorage('urlstring');

		if (!urlstring) {
			urlstring = 'parentSite:www.pella.com';
		}
		if (urlstring.search('RTA_Source') === -1 && window !== undefined && window.innerWidth < 790) {
			urlstring += '^^RTA_Source:Mobile';
		}
		if (apptInfo.appointmentType === 'showroom') {
			urlstring += '^^reqType:SHOWROOM';
		} else {
			urlstring += '^^reqType:web';
		}
		if (urlstring.search('host') === -1 && typeof window !== 'undefined') {
			urlstring += `^^host:${window.location.host}`;
		}

		let appointmentData = {
			oppNo: apiData.recID || randomId(),
			firstname: autoCapitalize(formData.firstname),
			lastname: autoCapitalize(formData.lastname),
			address: address.address1,
			address2: address.address2,
			country: address.country,
			city: address.city,
			state: address.state,
			zip: address.postalCode,
			dayphone: formData.phone,
			phonetype: 'MOBILE',
			email: formData.email,
			urlstring: urlstring || null,
			apptsource: RTA_COMMON.INTERFACE.WEB,
			comments: comments || null,
			branchID: apiData.branchID,
			noOfWindows: formData.windows || null,
			noOfDoors: formData.doors || null,
			rtapage: referrer || formData.referrer || null,
			cartadd: getCookie('cartadd') || null,
			requestId: getLocalStorage('cheqResponse'),
			householdPets: formData.householdPets || undefined,
			recaptcha_response: captchaToken || null,
		};

		if (apptInfo.appointmentType === RTA_COMMON.TYPE.NRT) {
			appointmentData['appttype'] = 'NRT';
			appointmentData['consentcall'] = 'N';
			appointmentData['consentsms'] = 'N';
			appointmentData['notifyemail'] = 'N';
			appointmentData['consenttophone'] = 'N';
			appointmentData['consenttomail'] = 'N';
			dispatch(appointment(appointmentData));
		} else {
			if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				appointmentData['preferenceDay'] = apptInfo.preferenceDay;
				appointmentData['preferenceTime'] = apptInfo.preferenceTime;
				appointmentData['notifyemail'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consentcall'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consentsms'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consenttophone'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consenttomail'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['appttype'] = 'Preferred';
				dispatch(appointment(appointmentData));
			} else if ([RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.VIRTUAL, RTA_COMMON.TYPE.SHOWROOM].includes(apptInfo.appointmentType)) {
				appointmentData['start'] = apptInfo.startDate;
				appointmentData['end'] = apptInfo.endDate;
				appointmentData['notifyemail'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consentcall'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consentsms'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consenttophone'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['consenttomail'] = formData.contactCheck === true ? 'Y' : 'N';
				appointmentData['appttype'] = 'RT';
				appointmentData['storename'] = showroomInfo.showroomName || null;
				appointmentData['virtualapptmt'] = apptInfo.appointmentType === RTA_COMMON.TYPE.VIRTUAL ? true : false;

				if (apptInfo.appointmentType === RTA_COMMON.TYPE.SHOWROOM) {
					appointmentData['notifyemail'] = 'Y';
					appointmentData['consenttomail'] = 'Y';
				}

				dispatch(appointment(appointmentData));
			}
		}
		setCookie('ReqConsultation', true);
		setFormStatus({
			...formStatus,
			tempObject: appointmentData,
			submitDisabled: true,
		});
	};

	const handlePreferenceBackClick = (e) => {
		e.preventDefault();
		dispatch(resetRTACalls());
		setApptInfo({
			...apptInfo,
			appointmentType: apptInfo.prevAppointmentType ? apptInfo.prevAppointmentType : RTA_COMMON.TYPE.HOME, // PW-5453 - Added virtual
			prevAppointmentType: '', // PW-5453 - Added virtual
			startDate: '',
			endDate: '',
		});
		setApiData({
			...apiData,
			isInitial: true,
			timeslots: [],
			territoryGroup: '',
			branchID: '',
		});
		setFormStatus({ ...formStatus, loading: true });
		dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, formData.postal, apptInfo.appointmentType));

		setTimeout(() => {
			let path = '';
			if (apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE) {
				path = 'js-Preference';
			}
			// PW-5453 - Added virtual
			else if ([RTA_COMMON.TYPE.HOME, RTA_COMMON.TYPE.VIRTUAL].includes(apptInfo.appointmentType)) {
				path = 'js-Calendar';
			}
			if (path !== '' && layout !== 'modal') {
				smoothScroll.scrollTo(path, -45);
			}
		}, 500);
	};

	const handleModalClose = () => {
		setModalOpen(false);
		setUsePreviousAddress(true);
		setComplete(false);
		setFormStatus({ ...formStatus, submitDisabled: true });
	};

	const handleModalZipChange = () => {
		dispatch(resetRTACalls());
		setModalOpen(false);
		const previousAddress = {
			...address,
			line_1: address.address1,
			line_2: address.address2,
			postcode: address.postalCode,
		};
		const newAppointmentType = apptInfo.aptSelected === RTA_COMMON.TYPE.VIRTUAL ? RTA_COMMON.TYPE.VIRTUAL : RTA_COMMON.TYPE.HOME;
		setFormData({
			...formData,
			postal: address.postalCode,
			previousAddress,
		});
		setApptInfo({
			...apptInfo,
			appointmentType: newAppointmentType,
			aptSelected: newAppointmentType,
		});
		setApiData({
			...apiData,
			branchID: '',
			territoryGroup: '',
			timezoneCode: '',
			timeslots: [],
			isInitial: true,
			waitingOnShowrooms: true,
		});
		setFormStatus({
			...formStatus,
			loading: true,
			hideShowrooms: true,
			showroomAlert: false,
			submitDisabled: true,
		});
		setComplete(false);
		dispatch(getCalendar(RTA_COMMON.INTERFACE.WEB, address.postalCode, newAppointmentType));
	};

	const handleAddressSubmit = (e) => {
		const isValid = validateAddress();
		if (isValid) {
			const previousAddress = {
				...address,
				line_1: address.address1,
				line_2: address.address2,
				postcode: address.postalCode,
			};
			loadCalendar(e);
			setShouldRenderForm(true);
			setLocation(address.postalCode);
			setFormData({
				...formData,
				postal: address.postalCode,
				previousAddress,
			});
		}
	};

	const isCalendarAppt = mounted && (apiData.isInitial || apptInfo.appointmentType === RTA_COMMON.TYPE.HOME) && shouldRenderForm;
	const isNRTAppt = mounted && apptInfo.appointmentType === RTA_COMMON.TYPE.NRT; //zip code 52001
	const isPreferenceAppt = mounted && apptInfo.appointmentType === RTA_COMMON.TYPE.PREFERENCE; //zip code 52242
	const FIFTEEN_MINUTE_ANCHOR = '15MinConnect';

	const isExiting = useExitIntent(shouldRenderForm);
	const token = getLocalStorage('okta-token-storage')?.accessToken?.accessToken || null;
	const appointmentBranchIDs = professional?.branch_id?.split(', ');
	const [hasAppointment, setHasAppointment] = useState(false);
	const [show15MinAppt, setShow15MinAppt] = useState(false);
	const [showing15MinApptCalendar, setShowing15MinApptCalendar] = useState(false);
	const [canHaveAppt, setCanHaveAppt] = useState(false);

	const update15MinParent = (value) => {
		setShowing15MinApptCalendar(value);
	};

	const [getAppointments, { data, error }] = useLazyQuery(MY_APPOINTMENTS, {
		context: { headers: { authorization: `Bearer ${token}` } },
		onError: (error) => {
			const scope = getCurrentScope();
			scope.setTransactionName('getAppointments');
			captureException(error || new Error('Unknown Error Details'));
		},
		onCompleted: (data) => {
			setHasAppointment(data?.myAppointments?.length > 0);
		},
	});

	useEffect(() => {
		if (oktaInitialized && token) {
			getAppointments();
		}
	}, [oktaInitialized, token, getAppointments]);

	useEffect(() => {
		if (branchId && appointmentBranchIDs) {
			setCanHaveAppt(appointmentBranchIDs?.includes(branchId));
		}
	}, [branchId, appointmentBranchIDs]);

	useEffect(() => {
		if ((canHaveAppt && !hasAppointment && !isPro) || (!canHaveAppt && showing15MinApptCalendar)) {
			setShow15MinAppt(true);
		} else {
			setShow15MinAppt(false);
		}
	}, [canHaveAppt, hasAppointment, isPro, showing15MinApptCalendar]);

	useEffect(() => {
		if (show15MinAppt && location.hash && location.hash === `#${FIFTEEN_MINUTE_ANCHOR}`) {
			update15MinParent(true);
		}
	}, [show15MinAppt, location.hash]);

	const [submitPreRtaForm] = useMutation(SUBMIT_PRE_RTA_FORM);
	const [preRtaSubmitted, setPreRtaSubmitted] = useState(false);
	const isPreRta = renderAs === 'page' && ['717', '710'].includes(branchId);

	const onPreRtaSubmit = async () => {
		const errors = validateContactInfo();

		if (Object.keys(errors).length > 0) {
			setErrors(errors);
			return;
		}

		setPreRtaSubmitted(true);

		await submitPreRtaForm({
			variables: {
				input: {
					data: {
						branchId: branchId,
						contactPhone: formData.phone,
						email: formData.email,
						firstName: formData.firstname,
						lastName: formData.lastname,
					},
				},
			},
		});
	};

	const [captchaToken, setCaptchaToken] = useState(null);

	return (
		<>
			{!branchData?.hide_promo && renderAs === 'page' && (
				<PromoBanner
					isCollapsible={true}
					staticPromos={branchData?.static_promo}
					type='header'
				/>
			)}

			<Container
				id='rta_form'
				noPadding={renderAs === 'form-only'}
			>
				{loading && <LoadingOverlay />}
				{renderAs === 'page' && (
					<Breadcrumbs
						data={breadcrumbs}
						currentUrl={currentUrl}
						className='[&>div]:p-0'
					/>
				)}
				{renderAs !== 'form-only' && <h1 className='my-4 md:my-6 xl:mb-8'>{body_copy.heading}</h1>}

				<div className='mb-4 flex flex-wrap gap-6 max-md:flex-col xl:gap-4'>
					{renderAs !== 'form-only' && (
						<div className='md:w-1/2'>
							<div className='text-subtitle-2 mb-4 flex w-fit items-center gap-x-0.5 rounded bg-gray-10 px-2 py-0.5 text-gray-70 md:mb-5 md:gap-x-1'>
								<Icon
									name='alarm'
									size={14}
									weight={700}
								/>
								<div
									className='rta-subheading-copy'
									dangerouslySetInnerHTML={{
										__html: body_copy.subheading,
									}}
								/>
							</div>
							<div
								className='rta-reschedule-copy'
								dangerouslySetInnerHTML={{
									__html: body_copy.reschedule,
								}}
							/>
							<div
								className='rta-description-copy mt-4 [&>ul]:text-subtitle-2 [&>ul]:mb-0 [&>ul]:mt-6 [&>ul]:list-none [&>ul]:pl-2.5 [&>ul]:xl:mt-8 [&_li]:flex [&_li]:items-start [&_li]:gap-x-2.5'
								dangerouslySetInnerHTML={{
									__html: body_copy.description,
								}}
							/>
						</div>
					)}
					<div className={`flex flex-col self-center bg-gray-5 p-6 md:p-8 ${isPreRta ? 'w-full lg:flex-1' : 'md:flex-1'}`}>
						{isPreRta && (
							<Row>
								<ContactInfoFields
									handleBlur={handleBlur}
									handleChange={handleChange}
									handleNumberChange={handleNumberChange}
									formData={formData}
									errors={errors}
									formLabels={form_labels}
								/>

								{!preRtaSubmitted && (
									<Column xs={12}>
										<Button
											color='primary-alt'
											onClick={onPreRtaSubmit}
										>
											Next
										</Button>

										{branchData?.pre_rta && <p className='text-caption mt-4 text-pretty text-gray-60'>{branchData.pre_rta}</p>}
									</Column>
								)}
							</Row>
						)}

						{(!isPreRta || preRtaSubmitted) && (
							<>
								{renderAs !== 'form-only' && <p className='mb-5'>{address_validation.heading}</p>}

								{Object.keys(addressErrors).length > 0 && (
									<Alert
										type='error'
										aria-live='assertive'
										className='mb-4'
										hideClose
									>
										<p>{address_validation.error_correct}</p>
										<List className='list-inside pl-0'>
											{Object.values(addressErrors).map((err, index) => (
												<li
													key={index}
													dangerouslySetInnerHTML={{
														__html: err,
													}}
												/>
											))}
										</List>
									</Alert>
								)}

								<AddressValidation
									allowInternational={true}
									defaultedPostalCode={formData.postal}
									addressComplete={[addressComplete, setAddressComplete]}
									addressInputs={{
										line1: {
											label: address_validation.address1,
											error: errors?.address1 ? errors?.address1 : 'Invalid address: PO Boxes are not accepted. Enter a physical address.',
											xs: 12,
										},
										line2: { visible: true, xs: 12 },
										city: { xs: 12, xl: 4 },
										state: { xs: 12, sm: 6, xl: 4 },
										postalCode: { xs: 12, sm: 6, xl: 4 },
									}}
									addressInput={formData.previousAddress}
									addressOutput={[address, setAddress]}
									addressModified={[addressModified, setAddressModified]}
									addressValidate={[forceAddressValidation, setForceAddressValidation]}
									usePreviousAddress={[usePreviousAddress, setUsePreviousAddress]}
									className={'[&_li]:px-3 [&_li]:py-1.5 hover:[&_li]:cursor-pointer hover:[&_li]:bg-gray-5 hover:[&_li]:underline [&_ul]:absolute [&_ul]:top-24 [&_ul]:z-1 [&_ul]:m-0 [&_ul]:list-none [&_ul]:border [&_ul]:border-gray-15 [&_ul]:bg-white [&_ul]:p-0 [&_ul]:shadow-lg'}
								/>
								<Button
									className='mx-auto mt-2 sm:p-4 lg:mr-0'
									color='primary-alt'
									onClick={handleAddressSubmit}
								>
									{address_validation.cta}
								</Button>
							</>
						)}
					</div>
				</div>

				{shouldRenderForm && (
					<div className={renderAs === 'form-only' ? 'px-6 pb-px md:px-8' : ''}>
						{isCalendarAppt && (
							<h3
								className='mb-4'
								id='js-Calendar'
							>
								{address_validation.date_time}
							</h3>
						)}

						{isPreferenceAppt && (
							<h3
								className='mb-4'
								id='js-Preference'
							>
								{address_validation.preferred_date_time}
							</h3>
						)}

						{isNRTAppt && <h3 className='mb-4'>{calendar.unavailable}</h3>}

						{shouldRenderForm && Object.keys(errors).length > 0 && (
							<Alert
								type='error'
								aria-live='assertive'
								className='mb-4'
								hideClose
							>
								<p>{address_validation.error_correct}</p>
								<List>
									{Object.values(errors).map((err, index) => {
										if (!err) return null;
										return (
											<li
												key={index}
												dangerouslySetInnerHTML={{
													__html: err,
												}}
											/>
										);
									})}
								</List>
							</Alert>
						)}

						<form
							noValidate
							action=''
							method='POST'
						>
							{isPreferenceAppt && (
								<Row className='mb-6'>
									<Column
										xs='12'
										md='6'
									>
										<Select
											error={!!errors.preferenceDay}
											errorText={RTA_ERRORS.PREFERRED_DAY_REQUIRED}
											id='preferenceDay'
											onChange={handlePreferenceChange}
											onBlur={handleBlur}
											required
											labelText={preference_appointment.preferred_day}
											value={apptInfo.preferenceDay}
										>
											<option value=''>{`${preference_appointment.heading}${htmlDecode('&hellip;')}`}</option>
											{preference_appointment.days.map((day, index) => (
												<option
													key={index}
													value={day}
												>
													{day}
												</option>
											))}
										</Select>
									</Column>

									<Column
										xs='12'
										md='6'
									>
										<Select
											error={!!errors.preferenceTime}
											errorText={RTA_ERRORS.PREFERRED_TIME_REQUIRED}
											id='preferenceTime'
											onChange={handlePreferenceChange}
											onBlur={handleBlur}
											required
											labelText={preference_appointment.preferred_time}
											value={apptInfo.preferenceTime}
										>
											<option value=''>{`${preference_appointment.heading}${htmlDecode('&hellip;')}`}</option>
											{preference_appointment.times.map((time, index) => (
												<option
													key={index}
													value={time}
												>
													{time}
												</option>
											))}
										</Select>
									</Column>

									{apiData.timeslots.length > 0 && (
										<Column xs='12'>
											<p>{figured_out}</p>
											<Button
												color='tertiary-alt'
												onClick={handlePreferenceBackClick}
											>
												{go_back}
											</Button>
										</Column>
									)}
								</Row>
							)}

							{isCalendarAppt && (
								<div className={`mb-10 flex flex-col ${renderAs === 'form-only' ? '' : 'px-20 xl:flex-row xl:gap-20 xl:px-0'}`}>
									{(apiData.isInitial || formStatus.loading) && <LoadingOverlay fixed={false} />}
									{(apiData.isInitial || apiData.timeslots?.length > 0) && (
										<APPTCalendar
											timeslots={apiData.isInitial ? apiData.initialCalendar : apiData.timeslots}
											handleSelect={handleDaySelect}
											selectedDay={apiData.isInitial ? new Date().getDate() : apptInfo.selectedDay}
											disabled={formStatus.loading || apiData.isInitial}
										/>
									)}

									<hr className='mb-8 mt-6 md:mb-12 md:mt-10 xl:hidden' />

									<div className=' w-full'>
										<p className='text-body-2 mb-1 flex items-center text-gray-60'>
											<Icon
												name='schedule'
												size={16}
												className='m-2'
											/>
											{`${address_validation.times} ${apiData.timezoneCode}`}
										</p>
										{apiData.isInitial || (apiData.timeslots?.length > 0 && apptInfo.arrayStartPosition != null && apptInfo.timeslotLength != null) ? (
											<APPTTimeSlots
												timeslots={apiData.isInitial ? apiData.initialCalendar : apiData.timeslots}
												arrayStartPosition={apiData.isInitial ? 0 : apptInfo.arrayStartPosition}
												selectedIndex={apiData.isInitial ? 0 : apptInfo.selectedTimeslot}
												timeslotLength={apiData.isInitial ? 3 : apptInfo.timeslotLength}
												handleSelect={handleTimeslotSelect}
												disabled={formStatus.loading || apiData.isInitial}
												initial={apiData.isInitial}
											/>
										) : (
											<APPTTimeSlots
												timeslots={apiData.isInitial ? apiData.initialCalendar : apiData.timeslots}
												arrayStartPosition={0}
												selectedIndex={-1}
												timeslotLength={0}
												handleSelect={handleTimeslotSelect}
												disabled={formStatus.loading || apiData.isInitial}
												initial={apiData.isInitial}
											/>
										)}
									</div>
								</div>
							)}

							<Row className={`mb-10 ${renderAs === 'form-only' ? '' : 'xl:max-w-[75%]'}`}>
								{!isPreRta && (
									<>
										<Column xs={12}>
											<h3 className='mb-4 w-full'>{form_heading}</h3>
										</Column>

										<ContactInfoFields
											handleBlur={handleBlur}
											handleChange={handleChange}
											handleNumberChange={handleNumberChange}
											formData={formData}
											errors={errors}
											formLabels={form_labels}
										/>
									</>
								)}

								<Column
									xs='12'
									className='hidden'
								>
									<Select
										id='householdPets'
										value={formData.householdPets}
										onChange={handleChange}
										labelText='Do you have pets?'
										autoComplete='off'
									>
										<option value=''>Please Select...</option>
										<option
											id='householdPetsNo'
											value='No'
										>
											No
										</option>
										<option
											id='householdPetsYes'
											value='Yes'
										>
											Yes
										</option>
									</Select>
								</Column>

								<Column xs='12'>
									<TextArea
										groupClass='[&_p]:text-caption [&_p]:text-gray-60'
										id='comments'
										type='text'
										assistiveText={form_labels.project_details.description}
										onChange={handleChange}
										labelText={form_labels.project_details.heading}
										value={formData.comments}
										autoComplete='off'
									/>
								</Column>

								<Column xs='12'>
									<Checkbox
										groupClass='[&_label]:items-start [&_label]:text-body-2'
										type='checkbox'
										aria-required='true'
										id='privacyCheck'
										checked={formData.privacyCheck}
										onChange={handleCheck}
										error={errors.privacyCheck ? true : false}
										errorText={RTA_ERRORS.PRIVACY_REQUIRED}
										labelText={
											<div
												dangerouslySetInnerHTML={{
													__html: checkboxes.privacy,
												}}
											/>
										}
									/>
								</Column>

								<Column
									xs='12'
									className=' mb-6'
								>
									<Checkbox
										groupClass='[&_label]:items-start [&_label]:text-body-2'
										type='checkbox'
										id='contactCheck'
										checked={formData.contactCheck}
										onChange={handleCheck}
										labelText={
											<div
												dangerouslySetInnerHTML={{
													__html: checkboxes.text_messaging,
												}}
											/>
										}
									/>
									<p className='text-body-2'>{checkboxes.confirmation}</p>
								</Column>

								<Column
									xs='12'
									className='flex flex-col justify-end md:flex-row'
								>
									{captchaBranches.includes(branchId) && (
										<Recaptcha
											classes='mx-auto max-md:mb-4 md:mr-4'
											token={[captchaToken, setCaptchaToken]}
											src={src}
										/>
									)}
									<Button
										id='scheduleAppointment'
										type='button'
										color='primary-alt'
										onClick={handleSubmit}
									>
										{checkboxes.cta}
									</Button>
								</Column>
							</Row>
						</form>
					</div>
				)}

				{renderAs === 'page' && (
					<div className={`flex  ${!isDesktop || showing15MinApptCalendar ? 'flex-col items-center gap-y-10 md:gap-y-2' : 'flex-row justify-center gap-x-3'}`}>
						{show15MinAppt && <ContactCard view='rta' />}
						{!show15MinAppt && <HoverBannerRTA />}
					</div>
				)}

				{renderAs === 'page' && show15MinAppt && isExiting && shouldRenderForm && (
					<FifteenMinuteApptExitIntent
						branchID={branchId}
						update15MinParent={update15MinParent}
						view='exit_intent'
					/>
				)}
			</Container>

			{/* Postal Code Change Modal */}
			<Modal
				width={500}
				isOpen={modalOpen}
				onClose={handleModalClose}
				effect='slideup'
			>
				<Modal.Content className='my-6 text-center'>
					<p>{postal_code_change.heading}</p>
					<p className='text-subtitle my-1.5 font-semibold'>{postal_code_change.confirm}</p>
					<Button
						className='mt-2 sm:mr-2'
						color='primary-alt'
						onClick={handleModalZipChange}
					>
						{postal_code_change.confirm_yes}
					</Button>
					<Button
						className='mt-2 sm:ml-2'
						color='primary-alt'
						type='button'
						onClick={handleModalClose}
					>
						{postal_code_change.confirm_no}
					</Button>
				</Modal.Content>
			</Modal>

			{/* Session Timeout Modal */}
			<Modal
				width={500}
				isOpen={sessionTimerModal}
				onClose={handleSessionModalClose}
				effect='slideup'
			>
				<Modal.Content className='my-6 text-center'>
					<p className='text-subtitle my-1.5 font-semibold'>{session_timeout.heading}</p>
					<p className='mb-4'>{session_timeout.description}</p>
					<Button
						color='primary-alt'
						onClick={handleSessionModalClose}
					>
						{session_timeout.timeout_ok}
					</Button>
				</Modal.Content>
			</Modal>
		</>
	);
};

ScheduleConsultation.propTypes = {
	content: PropTypes.object,
	renderAs: PropTypes.oneOf(['page', 'component', 'form-only']),
	src: PropTypes.string,
};

export default ScheduleConsultation;

export const query = graphql`
	query {
		contentstackStandardRta(uid: { eq: "bltfc3fa65c149fc29d" }) {
			...RTAEntry
		}
		contentstackRtaForm {
			captcha
			checkboxes {
				cta
				confirmation
				privacy
				text_messaging
			}
			form_labels {
				project_details {
					description
					heading
				}
				phone
				last_name
				first_name
				email
			}
			form_heading
		}
		contentstackConversionConfig {
			professional {
				branch_id
			}
		}
	}
`;
